"use client";
import { useEffect, useRef } from "react";

interface DonutChartData {
  label: string;
  value: number;
  color: string;
}

interface DonutChartProps {
  data?: DonutChartData[];
  title?: string;
  centerText?: string;
  centerValue?: string;
  className?: string;
}

const defaultData = [
  { label: "Desktop", value: 45, color: "#1F2937" },
  { label: "Mobile", value: 35, color: "#4B5563" },
  { label: "Tablet", value: 20, color: "#6B7280" }
];

export function DonutChart({
  data = defaultData,
  title = "User Devices",
  centerText = "Total Users",
  centerValue = "100%",
  className = ""
}: DonutChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas dimensions
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    // Clear canvas and set background
    ctx.clearRect(0, 0, rect.width, rect.height);

    // Set background color based on theme
    const isDarkMode = document.documentElement.classList.contains('dark');
    if (isDarkMode) {
      ctx.fillStyle = '#111827'; // dark:bg-gray-900
      ctx.fillRect(0, 0, rect.width, rect.height);
    } else {
      ctx.fillStyle = '#ffffff'; // bg-white
      ctx.fillRect(0, 0, rect.width, rect.height);
    }

    // Calculate total
    const total = data.reduce((sum, item) => sum + item.value, 0);

    // Draw donut chart
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const radius = Math.min(centerX, centerY) * 0.8;
    const innerRadius = radius * 0.6;

    let startAngle = -Math.PI / 2; // Start from top

    data.forEach((item) => {
      // Calculate angle
      const angle = (item.value / total) * (Math.PI * 2);

      // Draw arc
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, startAngle, startAngle + angle);
      ctx.arc(centerX, centerY, innerRadius, startAngle + angle, startAngle, true);
      ctx.closePath();

      // Fill with color
      ctx.fillStyle = item.color;
      ctx.fill();

      // Draw dividing line
      ctx.beginPath();
      ctx.moveTo(
        centerX + innerRadius * Math.cos(startAngle),
        centerY + innerRadius * Math.sin(startAngle)
      );
      ctx.lineTo(
        centerX + radius * Math.cos(startAngle),
        centerY + radius * Math.sin(startAngle)
      );
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 2;
      ctx.stroke();

      // Update start angle for next segment
      startAngle += angle;
    });

    // Draw center circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, innerRadius - 1, 0, Math.PI * 2);
    ctx.fillStyle = isDarkMode ? "#111827" : "#fff"; // Match background color
    ctx.fill();

    // Draw total in center
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = isDarkMode ? "#E5E7EB" : "#4B5563"; // Adjust text color for dark mode
    ctx.font = "bold 16px sans-serif";
    ctx.fillText(centerValue, centerX, centerY - 10);
    ctx.font = "12px sans-serif";
    ctx.fillStyle = isDarkMode ? "#9CA3AF" : "#9CA3AF"; // Lighter gray for both modes
    ctx.fillText(centerText, centerX, centerY + 10);

  }, [data, centerText, centerValue]);

  return (
    <div className={`h-full flex flex-col bg-white dark:bg-gray-900 overflow-hidden ${className}`}>
      <div className="mb-4 flex-shrink-0">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">{title}</h3>
      </div>
      <div className="flex-1 flex flex-col items-center justify-center min-h-0 overflow-hidden">
        <div className="flex-1 flex items-center justify-center max-h-[200px] max-w-[200px] w-full h-full">
          <canvas
            ref={canvasRef}
            className="h-full w-full max-h-full max-w-full"
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2 w-full flex-shrink-0 overflow-hidden">
          {data.slice(0, 4).map((item, index) => (
            <div key={index} className="flex flex-col items-center min-w-0">
              <div className="flex items-center gap-1 min-w-0">
                <div
                  className="h-2 w-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: item.color }}
                ></div>
                <span className="text-xs font-medium truncate">{item.label}</span>
              </div>
              <span className="text-xs text-gray-600 dark:text-gray-400">{item.value}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}